import { useCallback } from 'react';
import { useDispatch } from 'store/hooks';
import { updateProfile } from 'store/auth/actions';
import { FormValues, EditField, getFieldDisplayName } from 'pages/Profile/types';
import { UseFormReturn } from 'react-hook-form';

interface UseProfileActionsProps {
  methods: UseFormReturn<FormValues>;
  editField: EditField | null;
  setEditField: (field: EditField | null) => void;
  setError: (name: keyof FormValues, error: { message: string }) => void;
  setShowPhoneError: (show: boolean) => void;
  setPhoneError: (error: string | null) => void;
  // VRG-607 — `text` added: this local signature was narrower than the provider's
  // real `IAlertProps`, so a toast here could carry a headline but never the
  // sentence saying what to do next. That is why the failures in this file read
  // as bare log lines ("Failed to remove") — the type made the better copy
  // unexpressible.
  openSnackbar: (options: {
    severity: 'success' | 'error';
    title: string;
    text?: string;
  }) => void;
  profile: any;
  originalValues: Partial<FormValues>;
  onEmailVerificationRequested?: (newEmail: string, oldEmail: string) => void;
}

export const useProfileActions = ({
  methods,
  editField,
  setEditField,
  setError,
  setShowPhoneError,
  setPhoneError,
  openSnackbar,
  profile,
  originalValues,
  onEmailVerificationRequested,
}: UseProfileActionsProps) => {
  const dispatch = useDispatch();
  const { handleSubmit } = methods;

  const submitForField = async (key: EditField['key']): Promise<boolean> => {
    if (key === 'email') {
      const isValid = await methods.trigger('email', { shouldFocus: true });
      if (!isValid) return false;
      const email = (methods.getValues('email') ?? '').trim();
      onEmailVerificationRequested?.(email, profile?.email ?? '');
      return true;
    }
    if (key === 'password') {
      const isValid = await methods.trigger(['current_password', 'new_password'], {
        shouldFocus: true,
      });
      if (!isValid) return false;
    }
    const data = methods.getValues();
    const firstName = (data.first_name ?? '').trim();
    const lastName = (data.last_name ?? '').trim();

    const payload: any = {};
    switch (key) {
      case 'name':
        payload.first_name = firstName;
        payload.last_name = lastName;
        break;
      case 'username': {
        const usernameTrimmed = (data.username ?? '').trim();
        payload.username = usernameTrimmed;
        break;
      }
      case 'birthday':
        if (data.birthday instanceof Date) payload.birthday = data.birthday;
        break;
      case 'phone': {
        const input = data.phone ?? '';
        const cleaned = input.replace(/(?!^)[+]/g, '').replace(/[^+\d]/g, '');
        payload.phone = cleaned;
        break;
      }
      case 'gender':
        payload.gender = data.gender;
        break;
      case 'country':
        payload.country = data.country;
        break;
      case 'password':
        payload.current_password = data.current_password ?? '';
        payload.new_password = data.new_password ?? '';
        break;
      default:
        break;
    }

    if (typeof profile?.avatar === 'string' && profile.avatar.length > 0) {
      payload.avatar = profile.avatar;
    }

    return new Promise<boolean>(resolve => {
      dispatch(
        updateProfile(payload, ({ success, content }) => {
          if (success) {
            const fieldName = getFieldDisplayName(key);
            openSnackbar({ severity: 'success', title: `${fieldName} updated` });
            setEditField(null);
            resolve(true);
          } else {
            const details = (content && content.details) || {};
            const phoneKey = ['phone', 'phone_number', 'phoneNumber'].find(k =>
              Object.prototype.hasOwnProperty.call(details, k)
            ) as any;
            const backendMsg = phoneKey ? (details as any)[phoneKey] : content?.message;
            const msg: string =
              typeof backendMsg === 'string' && backendMsg.length > 0
                ? backendMsg
                : 'Enter a valid phone number';
            if (phoneKey) {
              setError('phone', { message: msg });
              setShowPhoneError(true);
              setPhoneError(msg);
            }
            resolve(false);
          }
        })
      );
    });
  };

  const onSubmitGeneralInfo = async (
    data: any,
    onSuccess: () => void,
    onError: (error: any) => void
  ) => {
    const firstName = (data.first_name ?? '').trim();
    const lastName = (data.last_name ?? '').trim();

    let phoneNumber = data.phone;
    if (!phoneNumber || (phoneNumber && phoneNumber.split(' ').length === 1)) {
      phoneNumber = null;
    }

    const payload: any = {
      first_name: firstName,
      last_name: lastName,
      username: data.username === '' ? null : data.username,
      birthday: data.birthday,
      gender: data.gender === '' ? null : data.gender,
      country: data.country,
      phone: phoneNumber,
    };

    dispatch(
      updateProfile(payload, ({ success, content }) => {
        if (success) {
          openSnackbar({ severity: 'success', title: 'Profile Updated' });
          setEditField(null);
          onSuccess();
        } else {
          // VRG-607 — "Failed to update profile" is log-line voice with no next
          // step. This reader is a shopper, not an operator.
          openSnackbar({
            severity: 'error',
            title: "We couldn't save your changes",
            text: 'Please try again.',
          });
          onError(content);
        }
      })
    );
  };

  const onSubmit = handleSubmit(async data => {
    const firstName = (data.first_name ?? '').trim();
    const lastName = (data.last_name ?? '').trim();

    const payload: any = {};
    switch (editField?.key) {
      case 'name':
        payload.first_name = firstName;
        payload.last_name = lastName;
        break;
      case 'username': {
        const usernameTrimmed = (data.username ?? '').trim();
        payload.username = usernameTrimmed;
        break;
      }
      case 'birthday':
        if (data.birthday instanceof Date) payload.birthday = data.birthday;
        break;
      case 'email': {
        const email = (data.email ?? '').trim();
        onEmailVerificationRequested?.(email, profile?.email ?? '');
        setEditField(null);
        return;
      }
      case 'phone': {
        const input = data.phone ?? '';
        const cleaned = input.replace(/(?!^)[+]/g, '').replace(/[^+\d]/g, '');
        payload.phone = cleaned;
        break;
      }
      case 'gender':
        payload.gender = data.gender;
        break;
      case 'country':
        payload.country = data.country;
        break;
      case 'password':
        payload.current_password = data.current_password ?? '';
        payload.new_password = data.new_password ?? '';
        break;
      default:
        break;
    }

    if (typeof profile?.avatar === 'string' && profile.avatar.length > 0) {
      payload.avatar = profile.avatar;
    }

    dispatch(
      updateProfile(payload, ({ success, content }) => {
        if (success) {
          const fieldName = getFieldDisplayName(editField?.key);
          openSnackbar({
            severity: 'success',
            title: `${fieldName} updated`,
          });
          setEditField(null);
        }

        if (!success) {
          // VERA-837: password failures were being routed to setError('phone', ...)
          // unconditionally, so a wrong current-password / weak new-password
          // response never surfaced anywhere in the password editor (it landed on
          // a field that isn't even rendered in that drawer). Route password
          // failures to the actual offending field instead; RHF's default
          // reValidateMode ('onChange') clears it again as the user retypes.
          if (editField?.key === 'password') {
            const backendMsg: string =
              typeof content?.message === 'string' && content.message.length > 0
                ? content.message
                : 'Failed to update password.';
            const isCurrentPasswordError = /current password/i.test(backendMsg);
            setError(isCurrentPasswordError ? 'current_password' : 'new_password', { message: backendMsg });
            return;
          }

          const details = (content && content.details) || {};
          const phoneKey = ['phone', 'phone_number', 'phoneNumber'].find(k =>
            Object.prototype.hasOwnProperty.call(details, k)
          ) as 'phone' | 'phone_number' | 'phoneNumber' | undefined;
          const backendMsg = phoneKey ? (details as any)[phoneKey] : content?.message;
          const msg: string =
            typeof backendMsg === 'string' && backendMsg.length > 0
              ? backendMsg
              : 'Enter a valid phone number';

          setError('phone', { message: msg });
          setShowPhoneError(true);
          setPhoneError(msg);
          return;
        }
      })
    );
  });

  const handleCancel = useCallback(() => {
    if (!editField) {
      setEditField(null);
      return;
    }
    switch (editField.key) {
      case 'name':
        methods.setValue(
          'first_name',
          (originalValues.first_name ?? methods.getValues('first_name')) as any
        );
        methods.setValue(
          'last_name',
          (originalValues.last_name ?? methods.getValues('last_name')) as any
        );
        break;
      case 'password':
        methods.setValue(
          'current_password',
          (originalValues.current_password ?? methods.getValues('current_password')) as any
        );
        methods.setValue(
          'new_password',
          (originalValues.new_password ?? methods.getValues('new_password')) as any
        );
        break;
      default: {
        const key = editField.key as keyof FormValues;
        const prev = (originalValues[key] ?? methods.getValues(key)) as any;
        methods.setValue(key, prev);
      }
    }
    setEditField(null);
  }, [editField, methods, originalValues, setEditField]);

  // Clear an optional field back to empty (sends an explicit null, which the
  // per-field save path doesn't do for e.g. birthday).
  const onRemoveField = useCallback(
    (key: 'username' | 'birthday' | 'gender' | 'country' | 'phone') => {
      const payload: any = { [key]: null };
      if (typeof profile?.avatar === 'string' && profile.avatar.length > 0) {
        payload.avatar = profile.avatar;
      }
      dispatch(
        updateProfile(payload, ({ success }) => {
          if (success) {
            methods.setValue(key as keyof FormValues, (key === 'birthday' ? null : '') as any);
            openSnackbar({ severity: 'success', title: `${getFieldDisplayName(key)} removed` });
            setEditField(null);
          } else {
            // VRG-607 — "Failed to remove" named no object, so the reader could
            // not tell WHAT had failed. The success line one branch up already
            // names the field; the failure now does too.
            openSnackbar({
              severity: 'error',
              title: `We couldn't remove your ${getFieldDisplayName(key).toLowerCase()}`,
              text: 'Please try again.',
            });
          }
        })
      );
    },
    [dispatch, methods, openSnackbar, profile, setEditField]
  );

  return {
    onSubmit,
    onSubmitForField: submitForField,
    handleCancel,
    onSubmitGeneralInfo,
    onRemoveField,
  };
};
